<template>
  <div class="content">
    <div class="content-wrap">
      <h3>{{ t('role-edit').toUpperCase() }}</h3>
      <div class="card-body">
        <Form
          @submit="onSubmit"
          :validation-schema="schema"
          v-slot="{ errors, isSubmitting }"
          :keep-values="true"
        >
          <div class="column d-flex justify-content-center role-data">
            <!-- LEWA KOLUMNA -->
            <div class="col-sm-6">
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('name2') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="name"
                    as="input"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': !!errors?.name }"
                    v-model="roleData.name"
                  />
                  <div class="invalid-feedback">{{ errors.name }}</div>
                </div>
              </div>
            </div>

            <!-- PRAWA KOLUMNA -->
            <div class="col-sm-6">
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="description"
                    as="textarea"
                    class="form-control"
                    :class="{ 'is-invalid': !!errors?.description }"
                    v-model="roleData.description"
                  />
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="column d-flex justify-content-center role-data">
            <div class="form-group row w-100">

              <div class="col-sm-12">
                <!-- ZAZNACZONE -->
                <p class="mb-2 text-muted" v-if="selectedPermissionsDetails.length">{{ t('selected-label') }}:</p>
                <div class="roles-tiles mb-3" v-if="selectedPermissionsDetails.length">
                  <label
                    v-for="perm in selectedPermissionsDetails"
                    :key="'sel-'+perm.name"
                    class="role-tile selected"
                  >
                    <!-- checkbox spięty z vee-validate (pole 'permissions') -->
                    <Field
                      name="permissions"
                      as="input"
                      type="checkbox"
                      class="sr-only"
                      :value="perm.name"
                      v-model="selectedPermissions"
                      :aria-checked="selectedPermissions.includes(perm.name)"
                      :aria-label="perm.name"
                    />
                    <div class="role-title">{{ perm.name }}</div>
                    <!-- Opis uprawnienia pochodzi z bazy (access.role.description) — tłumaczony słownikiem `db`. -->
                    <div class="role-desc" v-if="perm.description">{{ tdb(perm.description) }}</div>
                  </label>
                </div>

                <!-- DOSTĘPNE -->
                <p class="mb-2 text-muted">{{ t('available-label') }}:</p>
                <div class="roles-tiles" :class="{ 'is-invalid': !!errors?.permissions }" role="role" aria-label="permissions">
                  <label
                    v-for="perm in availablePermissions"
                    :key="'avail-'+perm.name"
                    class="role-tile"
                    :class="{
                      selected: selectedPermissions.includes(perm.name),
                      'role-tile-locked': !canGrant(perm.name),
                    }"
                    :title="!canGrant(perm.name)
                      ? t('permission-grant-blocked-tile')
                      : null"
                  >
                    <Field
                      name="permissions"
                      as="input"
                      type="checkbox"
                      class="sr-only"
                      :value="perm.name"
                      v-model="selectedPermissions"
                      :disabled="!canGrant(perm.name)"
                      :aria-checked="selectedPermissions.includes(perm.name)"
                      :aria-label="perm.name"
                    />
                    <div class="role-title">{{ perm.name }}</div>
                    <!-- Opis uprawnienia pochodzi z bazy (access.role.description) — tłumaczony słownikiem `db`. -->
                    <div class="role-desc" v-if="perm.description">{{ tdb(perm.description) }}</div>
                  </label>
                </div>

                <div class="invalid-feedback d-block">{{ errors.permissions }}</div>
              </div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center actions">
            <div class="col-auto" v-if="processError">
              <div class="process-error form-control">{{ processError }}</div>
            </div>
            <div class="col-auto" v-if="processSuccess">
              <div class="process-success form-control">{{ processSuccess }}</div>
            </div>
            <div class="col-auto buttons">
              <button type="button" @click="goBack" class="btn button-add-user">{{ t('cancel') }}</button>
              <button class="btn button-save" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                {{ t('save').toUpperCase() }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>
  
<script setup>
import moment from "moment";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useI18n } from "vue-i18n";
  
moment.locale("pl");
const { t } = useI18n();

// Yup - schemat walidacji (dynamicznie tłumaczony!)
const schema = Yup.object({
  name: Yup.string().required(t('user-username-required')),
  description: Yup.string(),
  permissions: Yup.array().of(Yup.string()).min(1, t('user-permissions-required')),
});
</script>

<script>
// import { ref } from "vue";
import RolesService from "@/services/Roles";
import { useUserStore } from "@/stores/user.store";

export default {
  name: "EditRole",
  props: { roleId: { required: true } }, // ID edytowanej roli
  data() {
    return {
      rolesAvailable: [], // lista ról do wyboru
      catalogPermissions: [], // pełny katalog uprawnień (access.role) — także nieprzypisane do grup
      roleData: { name: "", description: "" }, // główny obiekt formularza (zassany z API)
      selectedPermissions: [], // tablica zaznaczonych uprawnień (checkboxy)
      processError: "", // komunikat błędu zapisu
      processSuccess: "", // komunikat sukcesu
    };
  },
  created() {
    // Pobierz role z API i zapisz je do rolesAvailable
    RolesService.getRolesList()
    .then((data) => {
      this.rolesAvailable = Array.isArray(data) ? data : [];
    })
    .catch((e) => (this.processError = e?.message || String(e)));

    // Pobierz pełny katalog uprawnień (żeby pokazać także nowo dodane, jeszcze nieprzypisane do grup)
    RolesService.getAllPermissions()
    .then((data) => {
      this.catalogPermissions = Array.isArray(data) ? data : [];
    })
    .catch((e) => console.error("Nie udało się pobrać katalogu uprawnień:", e));

    // 2) Pobierz edytowaną grupę i ustaw wartości pól + zaznaczenia
    RolesService.fetchRole(this.roleId)
    .then((data) => {
      const item = Array.isArray(data) ? data[0] : data;

      // trzymaj permissions jako OBIEKTY (żeby mieć description)
      const permsAsObjects =
        Array.isArray(item?.permissions)
          ? item.permissions.map(p =>
              typeof p === "string" ? { name: p, description: "" } : p
            )
          : [];

      this.roleData = {
        name: item?.name ?? "",
        description: item?.description ?? "",
        permissions: permsAsObjects, // obiekty!
      };

      // preselect – zaznacz to, co ma grupa (nazwy)
      this.selectedPermissions = permsAsObjects.map(p => p.name);
    })
    .catch((e) => (this.processError = e?.message || String(e)));
  },
  methods: {
    goBack() {
      this.$emit("goBack"); // powrót do listy ról
    },
    // wyciągnij numer ID z obiektu permission
    getPermId(p) {
      if (p && typeof p.id === 'number') return p.id;         // jeśli backend zwróci id
      const m = (p?.name ?? '').match(/\d+/);                  // np. "ROLE_27" -> "27"
      const n = m ? parseInt(m[0], 10) : NaN;
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER; // brak numeru -> na koniec
    },
    sortByPermId(a, b) {
      const da = this.getPermId(a);
      const db = this.getPermId(b);
      if (da !== db) return da - db;
      // tie-breaker po nazwie
      return (a?.name ?? '').localeCompare(b?.name ?? '');
    },
    // Czy aktualny admin może nadać dane uprawnienie (ROLE_17/18/19 wymagają posiadania).
    canGrant(name) {
      return this.userStore.canGrantPermission(name);
    },
    onSubmit(values) {
      this.processSuccess = "";
      this.processError = "";
      // defensywnie: jeśli z jakiegoś powodu Vee nie poda permissions,
      // weź z local state (selectedPermissions)
      const perms = Array.from(new Set(values.permissions ?? this.selectedPermissions ?? []));

      // Defensywnie: także po stronie klienta odfiltrowujemy uprawnienia, których admin
      // nie może nadać. Backend i tak rzuca AccessDeniedException, ale zatrzymujemy
      // żądanie wcześniej z czytelnym komunikatem.
      const blocked = perms.filter((name) => !this.userStore.canGrantPermission(name));
      if (blocked.length) {
        this.processError = this.$t('permission-grant-blocked-error', {
          perms: blocked.join(', '),
        });
        return;
      }

      const payload = {
        name: (values.name || "").trim(),
        description: (values.description || "").trim(),
        permissions: perms, // ["ROLE_10", "ROLE_11", ...]
      };

      // call do API
      RolesService.updateRole(this.roleId, payload)
        .then(async () => {
          this.processSuccess = "Zapisano zmiany.";
          // Przeładuj dane zalogowanego użytkownika — jeśli edytowano grupę, do której należy,
          // nowe uprawnienia (np. ROLE_10) zaczną działać od razu, bez przelogowania (menu i
          // dostęp do widoków przeliczą się reaktywnie).
          try {
            await this.userStore.self(true);
          } catch (e) {
            console.error("Nie udało się odświeżyć danych użytkownika po zmianie uprawnień:", e);
          }
          this.goBack(); // jeśli chcesz wracać do listy
        })
        .catch((err) => {
          console.error(err);
          this.processError = err?.response?.data?.message || err?.message || "Błąd zapisu.";
        });
    },
  },
  computed: {
    // Store przez getter — w SFC z `<script setup>` Options-API `setup()` nie jest
    // uruchamiany, więc `return { userStore }` daje `undefined` w `this`. Pinia
    // jest singletonem, więc wywołanie przy każdym dostępie nic nie kosztuje.
    userStore() {
      return useUserStore();
    },
    allPermissions() {
      const map = new Map();

      (this.rolesAvailable || []).forEach(g => {
        (g.permissions || []).forEach(p => {
          const obj = (typeof p === 'string') ? { name: p, description: '' } : p;
          if (!map.has(obj.name)) map.set(obj.name, obj);
        });
      });

      // Pełny katalog uprawnień (dokłada także nowe, jeszcze nieprzypisane do żadnej grupy).
      (this.catalogPermissions || []).forEach(p => {
        const obj = (typeof p === 'string') ? { name: p, description: '' } : p;
        if (!map.has(obj.name)) map.set(obj.name, obj);
      });

      if (map.size === 0 && Array.isArray(this.roleData.permissions)) {
        this.roleData.permissions.forEach(p => {
          const obj = (typeof p === 'string') ? { name: p, description: '' } : p;
          if (!map.has(obj.name)) map.set(obj.name, obj);
        });
      }

      // TU sortujemy raz globalnie – filtry zachowają kolejność
      return Array.from(map.values()).sort(this.sortByPermId);
    },

    selectedPermissionsDetails() {
      const set = new Set(this.selectedPermissions);
      return this.allPermissions.filter(p => set.has(p.name));
    },

    availablePermissions() {
      const set = new Set(this.selectedPermissions);
      return this.allPermissions.filter(p => !set.has(p.name));
    },
  },
};
</script>

<style scoped>
.sr-only {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0);
  white-space: nowrap; border: 0;
}

.roles-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-md);
}

.role-tile {
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  padding: 12px 14px;
  background: var(--surface-primary);
  cursor: pointer;
  user-select: none;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}

.role-tile:hover {
  border-color: var(--border-default);
  background: var(--surface-primary);
}

.role-tile.selected {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
  background: var(--accent-blue-soft);
}

.role-tile.role-tile-locked {
  opacity: .55;
  background: var(--surface-secondary);
  cursor: not-allowed;
  border-style: dashed;
}

.role-tile.role-tile-locked:hover {
  border-color: var(--border-default);
  background: var(--surface-secondary);
}

.role-title {
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
}

.role-desc {
  margin-top: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

/* czerwone obramowanie całej siatki, gdy jest błąd walidacji */
.roles-tiles.is-invalid {
  border: 1px dashed var(--status-error);
  border-radius: var(--panel-radius);
  padding: 8px;
}
  .col-form-label {
    text-align: right;
  }
  .process-error {
    color: darkred;
  }
  
  .process-success {
    color: var(--status-ok);
  }
  
  .content {
    width: 100%;
    min-height: 270px;
  }
  
  h3 {
    padding: 15px 25px;
    background-color: var(--surface-primary);
  }
  
  .card-body .column {
    padding: 20px 30px;
  }
  
  .card-body .column > div {
    padding: 5px 20px;
  }
  .role-table {
    width: 100%;
    font-family: "Ubuntu", sans-serif;
    font-style: normal;
    font-weight: var(--font-weight-regular);
    font-size: var(--font-size-xl);
    color: var(--text-primary);
  }
  
  .role-table th {
    background: var(--surface-primary);
    font-size: var(--font-size-lg);
    color: var(--text-primary);
    border-left: 1px solid rgb(214, 214, 215);
    border-bottom: 1px solid rgb(214, 214, 215);
  }
  
  .role-table td {
    background: rgb(214, 214, 215);
    font-size: var(--font-size-md);
    color: dimgrey;
    border-left: 1px solid var(--border-default);
  }
  .actions {
    margin: 10px 0;
  }
  .actions button {
    margin: 0 5px;
  }
  .role-data {
    border-bottom: 1px solid var(--border-default);
  }
  .invalid-feedback {
    font-size: var(--font-size-xs);
  }
  input[type="checkbox"] {
    margin-top: 13px;
  }
  
  .buttons {
    margin-top: .25rem;
    margin-bottom: 1rem;
  }
  
  .buttons button {
    padding: 0.5rem 2rem 0.5rem 2rem;
  }
  
  .button-save {
    border: 1px solid var(--status-ok) !important;
  }

  .button-save:hover {
    background-color: var(--status-ok) !important;
    border: 1px solid var(--status-ok) !important;
  }

  .button-add-user {
  border-radius: 4px;
}

.button-add-user:hover {
  background-color: var(--border-strong);
}
  </style>
  